import { Dtos, formatToPercent, type NaicsCodeId } from "@inrev/common";
import { ContractSuretyType } from "@inrev/common/lib/esm/enums";
import { useAdminUpdateBondRequest } from "@portal/api/bond-request/admin";
import { AdminBondRequest } from "@portal/api/bond-request/admin/types";
import { cn } from "@portal/components/lib/utils";
import { createFormResolver } from "@portal/components/multiform";
import { Button, ClickableIcon } from "@portal/components/primitives";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useVersionConflictSync } from "@portal/hooks/useVersionConflictSync";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { formatEmpty } from "../../../utils";
import {
	getFormCompletion,
	getNestedValue,
	getPartialFieldPaths,
	getRequiredForApprovalFieldPaths,
	hasOwnPath,
	isEmpty,
} from "../../../utils/schema-fields";
import { AccountPartialSubmitConfirmationModal } from "../../shared/AccountPartialSubmitConfirmationModal";
import {
	ContractDamagesEditItem,
	ContractHazardsEditItem,
} from "../../shared/ContractSelectionEditItems";
import {
	VIEW_SECTION_STICKY_HEADER_CLASS,
	ViewSectionLayout,
} from "../../shared/layouts/ViewSectionLayout";
import {
	ActualBidDateInput,
	BidAmountInput,
	BidDateInput,
	BondAmountCurrencyPercentInput,
	BondAmountInput,
	type BondRequestRowProps,
	CompletionBondInput,
	ContractAmountInput,
	ContractDateInput,
	FinalBondFormTypeDropdown,
	LowestBidInput,
	ProjectDescriptionBlockquote,
	ProjectDescriptionInput,
	ProjectLocationInput,
	ProjectNameInput,
	ScopeOfWorkInput,
	SecondLowestBidInput,
	SfaaCodeInput,
	TimeToCompleteInput,
	TListingRequiredInput,
	WarrantyPeriodInput,
} from "../../shared/request/BondRequestDetailRows";
import {
	VIEW_SECTION_CARD_STACK_CLASS,
	ViewSectionCardEditItem,
} from "../../shared/ViewSectionCardEditItem";
import { AdminBondingLineConflictModal } from "./AdminBondingLineConflictModal";
import {
	type ContractUpdateForm,
	getContractUpdateSchema,
	mapAdminBondRequestToContractUpdateForm,
} from "./mapAdminBondRequestToContractUpdateForm";

const ContractUpdateForm = Dtos.Admin.SuretyQuote.Contract.Update.Request;

type AdminBondRequestProjectCardProps = {
	request: AdminBondRequest;
	accountNaicsCodes: NaicsCodeId[];
};

type FormComponentProps = BondRequestRowProps<ContractUpdateForm>;

const EMPTY_NAICS_CODE_IDS: NaicsCodeId[] = [];

const BID_BOND_CURRENCY_PERCENT_NAMES = {
	referenceSourceName: "bidAmount",
	currencyName: "bondAmount",
	percentName: "bondAmountPercentOfBid",
	amountTypeName: "bidBondAmountType",
} as const;

export const AdminBondRequestProjectCard = ({
	request,
	accountNaicsCodes,
}: AdminBondRequestProjectCardProps) => {
	const [editing, setEditing] = useState(false);
	const requestRef = useRef(request);
	requestRef.current = request;
	const [partialSubmit, setPartialSubmit] = useState<{ percentComplete?: number } | null>(null);
	const [bondingLineConflictModalOpen, setBondingLineConflictModalOpen] = useState(false);
	const pendingSubmitDataRef = useRef<ContractUpdateForm | null>(null);

	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();

	const scopeOfWorkIds = useMemo(
		() => request.project?.scopeOfWork?.map((row) => row.id) ?? EMPTY_NAICS_CODE_IDS,
		[request.project?.scopeOfWork],
	);

	const isBid = request.contractSuretyType === ContractSuretyType.bid;
	const isFinal = request.contractSuretyType === ContractSuretyType.final;
	const isB2F = request.contractSuretyType === ContractSuretyType.bid_to_final;

	const schema = getContractUpdateSchema(request.contractSuretyType);

	const resolverSchema = isMinimalUIEnabled
		? ContractUpdateForm.schema.refine(
				(data) => typeof data.project?.name === "string" && data.project.name.trim().length > 0,
				{ error: "Required", path: ["project", "name"] },
			)
		: ContractUpdateForm.schema.refine(
				(data) =>
					typeof data.project?.description === "string" &&
					data.project.description.trim().length > 0,
				{ error: "Required", path: ["project", "description"] },
			);

	// useForm only reads defaultValues on mount, so build them once — the mapper does three
	// DateTime.fromISO parses and this card re-renders on every keystroke via formState.isDirty.
	const [initialValues] = useState(() => mapAdminBondRequestToContractUpdateForm(request));

	const formMethods = useForm<ContractUpdateForm>({
		defaultValues: initialValues,
		mode: "onBlur",
		reValidateMode: "onBlur",
		resolver: createFormResolver<ContractUpdateForm>(resolverSchema),
	});
	const { formState, handleSubmit } = formMethods;

	useVersionConflictSync(request.updatedAt, () => {
		if (!editing) formMethods.reset(mapAdminBondRequestToContractUpdateForm(request));
	});

	const { updateBondRequest, updateBondRequestIsLoading } = useAdminUpdateBondRequest(
		request.id,
		request.suretyAccountId,
		{
			onConflict: () => {
				formMethods.reset(mapAdminBondRequestToContractUpdateForm(requestRef.current));
				setEditing(false);
			},
			onBondingLineConflict: () => setBondingLineConflictModalOpen(true),
		},
	);
	const submitUpdate = (data: ContractUpdateForm) => {
		pendingSubmitDataRef.current = data;
		updateBondRequest(data, {
			onSuccess: () => {
				setEditing(false);
				pendingSubmitDataRef.current = null;
			},
		});
	};

	const onSubmit = (data: ContractUpdateForm) => {
		const emptyPaths = [
			...getPartialFieldPaths(schema, data),
			...getRequiredForApprovalFieldPaths(schema, data),
		].filter((path) => hasOwnPath(data, path) && isEmpty(getNestedValue(data, path)));

		if (emptyPaths.length > 0) {
			pendingSubmitDataRef.current = data;
			setPartialSubmit({ percentComplete: getFormCompletion(schema, data) });
			return;
		}
		submitUpdate(data);
	};

	const onConfirmedPartialSubmit = () => {
		const pendingData = pendingSubmitDataRef.current;
		setPartialSubmit(null);
		if (pendingData) submitUpdate(pendingData);
	};

	const onBondingLineConflictOverride = () => {
		setBondingLineConflictModalOpen(false);
		if (pendingSubmitDataRef.current) {
			updateBondRequest(pendingSubmitDataRef.current, {
				onSuccess: () => {
					setEditing(false);
					pendingSubmitDataRef.current = null;
				},
				confirm: true,
			});
		}
	};

	const onBondingLineConflictMoveToReview = () => {
		setBondingLineConflictModalOpen(false);
		if (pendingSubmitDataRef.current) {
			updateBondRequest(pendingSubmitDataRef.current, {
				onSuccess: () => {
					setEditing(false);
					pendingSubmitDataRef.current = null;
				},
				confirm: false,
			});
		}
	};

	const handleCancel = useCallback(() => {
		formMethods.reset(mapAdminBondRequestToContractUpdateForm(request));
		setEditing(false);
	}, [formMethods, request]);

	// Escape backs out of edit mode from anywhere in the card, so a long form doesn't force a
	// scroll back to the action bar just to abandon a change.
	useEffect(() => {
		if (!editing || updateBondRequestIsLoading) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") handleCancel();
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [editing, updateBondRequestIsLoading, handleCancel]);

	const componentProps: Omit<FormComponentProps, "name"> = {
		formMethods,
		editing,
		schema,
		partialRequiresSubmit: false,
		isMinimalUIEnabled,
	};

	const editDisabled = request.status === "pending";
	const editButton = (
		<ClickableIcon
			type="pencil-square"
			size="mini"
			color="secondary"
			aria-label="Edit project details"
			data-testid="admin-bond-request-project-edit"
			trackingId="bond-request-admin-project-edit"
			disabled={editDisabled}
			onClick={() => setEditing(true)}
		/>
	);

	return (
		<FormProvider {...formMethods}>
			<ViewSectionLayout
				header="Project Details"
				partial={false}
				partialActive={false}
				headerClassName={editing ? VIEW_SECTION_STICKY_HEADER_CLASS : undefined}
				button={
					editing ? (
						<div className="flex space-x-[10px]">
							<Button
								variant="outline"
								color="secondary"
								size="sm"
								disabled={updateBondRequestIsLoading}
								onClick={handleCancel}
							>
								Cancel
							</Button>
							<Button
								variant="solid"
								color="primary"
								size="sm"
								disabled={!formState.isDirty}
								loading={updateBondRequestIsLoading}
								onClick={handleSubmit(onSubmit)}
							>
								Save
							</Button>
						</div>
					) : (
						<span
							title={editDisabled ? "Requests that are pending cannot be edited" : undefined}
							className={
								editDisabled
									? "inline-flex cursor-not-allowed [&_button]:pointer-events-none"
									: undefined
							}
						>
							{editButton}
						</span>
					)
				}
			>
				<div className="w-full flex flex-col">
					<div
						className={cn(
							"w-full h-full bg-surface-base rounded-md",
							VIEW_SECTION_CARD_STACK_CLASS,
						)}
					>
						{isMinimalUIEnabled && (
							<>
								<ProjectNameInput
									{...componentProps}
									projectName={request.project.name}
									name="project.name"
								/>
								<ProjectDescriptionBlockquote
									{...componentProps}
									projectDescription={request.project.description}
									name="project.description"
								/>
							</>
						)}
						{isBid && (
							<>
								<BidDateInput {...componentProps} bidDate={request.bidDate} name="bidDate" />
								<BidAmountInput
									{...componentProps}
									bidAmount={request.bidAmount}
									name="bidAmount"
								/>
								<BondAmountCurrencyPercentInput
									{...componentProps}
									bondAmount={request.bondAmount}
									requiredForApproval={request.bondAmount <= 0}
									currencyPercentNames={BID_BOND_CURRENCY_PERCENT_NAMES}
									name="bondAmount"
								/>
								{request.bidBondAmountType === "percent" && !editing && (
									<ViewSectionCardEditItem
										label="Percent of Bid"
										name="bondAmountPercentOfBid"
										viewComponent={formatEmpty(formatToPercent(request.bondAmountPercentOfBid))}
										editComponent={formatEmpty(formatToPercent(request.bondAmountPercentOfBid))}
										{...componentProps}
									/>
								)}
								<FinalBondFormTypeDropdown
									{...componentProps}
									finalBondFormType={request.finalBondFormType}
									placeholder="Select final bond form type"
									name="finalBondFormType"
								/>
							</>
						)}
						{isB2F && (
							<>
								<LowestBidInput
									{...componentProps}
									actualBidAmount={request.actualBidAmount}
									name="actualBidAmount"
								/>
								<SecondLowestBidInput
									{...componentProps}
									secondBid={request.secondBid}
									name="secondBid"
								/>
								<ActualBidDateInput
									{...componentProps}
									actualBidDate={request.actualBidDate}
									name="actualBidDate"
								/>
								<ContractDateInput
									{...componentProps}
									contractDate={request.contractDate}
									name="contractDate"
								/>
							</>
						)}

						{(isFinal || isB2F) && (
							<>
								<BondAmountInput
									{...componentProps}
									bondAmount={request.bondAmount}
									requiredForApproval={request.bondAmount <= 0}
									name="bondAmount"
								/>
								<ContractAmountInput
									{...componentProps}
									contractAmount={request.contractAmount}
									name="contractAmount"
								/>
							</>
						)}
						<ScopeOfWorkInput
							{...componentProps}
							scopeOfWork={scopeOfWorkIds}
							accountNaicsCodes={accountNaicsCodes}
							name="project.scopeOfWork"
						/>
						<SfaaCodeInput
							{...componentProps}
							accountId={request.suretyAccountId}
							sfaaCode={request.sfaaCode}
							suretyType={request.suretyType}
							name="sfaaCodeId"
						/>

						{!isMinimalUIEnabled && (
							<ProjectDescriptionInput
								{...componentProps}
								projectDescription={request.project.description}
								name="project.description"
							/>
						)}
						<ProjectLocationInput
							{...componentProps}
							city={request.project.address?.city}
							state={request.project.address?.state}
							name={["project.city", "project.state"]}
						/>
						<TimeToCompleteInput
							{...componentProps}
							monthsToComplete={request.project.monthsToComplete}
							name="project.monthsToComplete"
						/>
						<WarrantyPeriodInput
							{...componentProps}
							contractWarrantyMonths={request.project.contractWarrantyMonths}
							name="project.contractWarrantyMonths"
						/>

						<ContractDamagesEditItem
							{...componentProps}
							name="project.contractDamages"
							values={request.project.contractDamages}
						/>
						<ContractHazardsEditItem
							{...componentProps}
							name="project.contractHazards"
							values={request.project.contractHazards}
						/>
						<CompletionBondInput
							{...componentProps}
							completionBond={request.completionBond}
							name="completionBond"
						/>
						<TListingRequiredInput
							{...componentProps}
							tListingRequired={request.tListingRequired}
							name="tListingRequired"
						/>
					</div>
					{partialSubmit && (
						<AccountPartialSubmitConfirmationModal
							context="view"
							userContext="admin"
							percentComplete={partialSubmit.percentComplete}
							onClose={() => setPartialSubmit(null)}
							onSubmit={() => onConfirmedPartialSubmit()}
						/>
					)}
					{bondingLineConflictModalOpen && (
						<AdminBondingLineConflictModal
							onDismiss={() => setBondingLineConflictModalOpen(false)}
							onMoveToReview={onBondingLineConflictMoveToReview}
							onOverride={onBondingLineConflictOverride}
						/>
					)}
				</div>
			</ViewSectionLayout>
		</FormProvider>
	);
};
